import { useLanguage } from "@/contexts/LanguageContext";
import { useBrand } from "@/contexts/BrandContext";
import { useAuthGate } from "@/hooks/useAuthGate";
import { useNavigate } from "react-router-dom";

export default function Footer() {
  const { t, language } = useLanguage();
  const { brand, localizedName } = useBrand();
  const navigate = useNavigate();

  const { requireAuth } = useAuthGate();

  return (
    <footer className="border-t border-border bg-card mt-16">
      <div className="container mx-auto px-4 py-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              {brand.logoType === "image" ? (
                <img
                  src={brand.logo}
                  alt={localizedName(language)}
                  className="h-8 w-8 object-contain"
                />
              ) : (
                <span className="text-2xl">{brand.logo}</span>
              )}
              <span className="font-heading text-xl font-extrabold text-primary">
                {localizedName(language)}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {t("footer.tagline")}
            </p>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-heading font-semibold mb-3 text-foreground">
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li>
                <a
                  href="/menu"
                  className="hover:text-primary transition-colors"
                   onClick={(e) => {
                    e.preventDefault();
                    navigate("/menu");
                  }}
                >
                  {t("nav.menu")}
                </a>
              </li>
              <li>
                <a
                  href="/branches"
                  className="hover:text-primary transition-colors"
                    onClick={(e) => {
                    e.preventDefault();
                    navigate("/branches");
                  }}
                >
                  {t("nav.branches")}
                </a>
              </li>
              <li>
                <a
                  href="/about"
                  className="hover:text-primary transition-colors"
                   onClick={(e) => {
                    e.preventDefault();
                    navigate("/about");
                  }}
                >
                  {t("nav.about")}
                </a>
              </li>
              <li>
                <a
                  href="/cart"
                  className="hover:text-primary transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    requireAuth(() => navigate("/cart"));
                  }}
                >
                  {t("nav.cart")}
                </a>
              </li>
            </ul>
          </div>

          {/* Social / Download */}
          <div>
            <h4 className="font-heading font-semibold mb-3 text-foreground">
              Get the App
            </h4>
            <div className="flex gap-2">
              {brand.appStoreUrl && (
                <a
                  href={brand.appStoreUrl}
                  className="rounded-lg bg-muted px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  App Store
                </a>
              )}
              {brand.googlePlayUrl && (
                <a
                  href={brand.googlePlayUrl}
                  className="rounded-lg bg-muted px-4 py-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Google Play
                </a>
              )}
            </div>
            <div className="flex gap-3 mt-4 text-muted-foreground">
              {brand.socialLinks.map((link) => (
                <a
                  key={link.platform}
                  href={link.url}
                  className="cursor-pointer hover:text-primary transition-colors"
                >
                  {link.platform === "twitter"
                    ? "𝕏"
                    : link.platform === "facebook"
                      ? "📘"
                      : "📸"}
                </a>
              ))}
            </div>
          </div>
        </div>
        <div className="mt-8 space-y-3 border-t border-border pt-6 text-center text-xs text-muted-foreground">
          <div>
            © {new Date().getFullYear()} {localizedName(language)}.{" "}
            {t("footer.rights")}
          </div>
          <div>
            Powered by{" "}
            <span className="font-semibold text-primary">AlKhawarizmi</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
